import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import { CheckCircle2, ShieldCheck } from "lucide-react";

export default function RequestForm() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { id, ref } | null
  const [form, setForm] = useState({
    category: sp.get("category") || "",
    postcode: sp.get("postcode") || "",
    name: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    description: "",
    when_needed: "this-week",
  });

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Please choose a service");
    setBusy(true);
    try {
      const { data } = await api.post("/leads", form);
      toast.success("Request received — verified pros are being notified");
      // Logged-in customers → straight to their live-quotes page.
      // Guests → success screen with a sign-in prompt so they can watch quotes.
      if (user && user.role === "customer" && data?.id) {
        nav(`/my-request/${data.id}`);
        return;
      }
      setDone({ id: data?.id, ref: data?.ref });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send request");
    } finally { setBusy(false); }
  };

  if (done) {
    const signInHref = `/login?redirect=${encodeURIComponent(`/my-request/${done.id || ""}`)}`;
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <Toaster position="top-center" />
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <CheckCircle2 size={64} className="mx-auto text-emerald-500" />
          <h1 className="font-display text-3xl font-bold mt-4">Thanks — we've got your request</h1>
          {done.ref && <div className="mt-1 text-sm text-slate-500">Reference <b>{done.ref}</b></div>}
          <p className="text-slate-600 mt-3">
            Verified local pros are being notified now. Sign in with the email you provided ({form.email}) to see quotes arrive live and pick the pro you want.
          </p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Link to={signInHref} className="btn-accent" data-testid="request-view-quotes">Sign in to view quotes</Link>
            <Link to={`/signup?role=customer&email=${encodeURIComponent(form.email)}&redirect=${encodeURIComponent(`/my-request/${done.id || ""}`)}`} className="btn-outline" data-testid="request-signup-quotes">Create free account</Link>
          </div>
          <div className="mt-6">
            <button onClick={() => { setDone(null); setForm({ ...form, description: "" }); }} className="text-sm text-emerald-600 font-semibold" data-testid="request-another">Send another request</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Toaster position="top-center" />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="inline-flex items-center gap-2 badge-verified mb-4"><ShieldCheck size={14} /> Free · No obligation</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Find a professional near you</h1>
        <p className="text-slate-600 mt-2">Tell us about the job — verified local pros will send you quotes to pick from.</p>

        <form onSubmit={submit} className="mt-6 card space-y-4">
          <div>
            <label className="label">Service needed</label>
            <select data-testid="req-category" value={form.category} onChange={set("category")} className="field" required>
              <option value="">Choose a service…</option>
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Postcode</label>
              <input data-testid="req-postcode" required value={form.postcode} onChange={set("postcode")} className="field" placeholder="e.g. L1 8JQ" />
            </div>
            <div>
              <label className="label">When do you need it?</label>
              <select data-testid="req-when" value={form.when_needed} onChange={set("when_needed")} className="field">
                <option value="asap">As soon as possible</option>
                <option value="this-week">This week</option>
                <option value="next-week">Next week</option>
                <option value="flexible">I'm flexible</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Your name</label>
            <input data-testid="req-name" required value={form.name} onChange={set("name")} className="field" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Phone</label>
              <input data-testid="req-phone" required value={form.phone} onChange={set("phone")} className="field" placeholder="+44 …" />
            </div>
            <div>
              <label className="label">Email</label>
              <input data-testid="req-email" type="email" required value={form.email} onChange={set("email")} className="field" />
            </div>
          </div>
          <div>
            <label className="label">Describe the job</label>
            <textarea data-testid="req-description" required rows={4} value={form.description} onChange={set("description")} className="field" placeholder="e.g. Two rats seen in the loft, need urgent treatment." />
          </div>
          <button data-testid="req-submit" disabled={busy} className="btn-accent w-full">
            {busy ? "Sending…" : "Send my request"}
          </button>
          <div className="text-xs text-slate-500 text-center">By submitting, you agree to be contacted by FixNear about your request.</div>
        </form>
      </div>
    </div>
  );
}
